import React, { useEffect, useMemo, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Search,
    Filter,
    ChevronDown,
    MoreVertical,
    Shield,
    UserCheck,
    UserX,
    Mail,
    Phone,
    MapPin,
    Calendar,
    Download,
    Trash2,
    Edit,
    Eye,
    X,
    CheckCircle,
    AlertTriangle,
    Users,
    ArrowUpDown,
    Ban,
    Lock,
    Unlock,
    AlertCircle,
    Clock,
    User,
    FileText,
    BadgeCheck,
    FileCheck,
    ExternalLink,
    UserIcon,
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import AdminHeader from "../../Headers/AdminHeader";
import { getAllVerifications, useAppDispatch } from "../../redux";
import { useSelector } from "react-redux";
import { VerificationStatusEnum } from "../../modules/shared/model";
import { selectLoaderValue } from "../../redux/Slices/loadingSlice";
import Spinner from "../../modules/shared/components/Spinner";
import VerificationRequestTable from "./VerificationRequestTable";
import VerificationDetailModal from "../Modals/VerificationDetailModal";
const statusStyles = {
    [VerificationStatusEnum.pending]: "bg-yellow-100 text-yellow-700",
    [VerificationStatusEnum.accepted]: "bg-green-100 text-green-700",
    [VerificationStatusEnum.rejected]: "bg-red-100 text-red-700",
};
const VerificationRequestsPage = () => {
    const [selectedRequests, setSelectedRequests] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [requestType, setRequestType] = useState("all");
    const [currentUserInfo, setCurrentUserInfo] = useState({});
    const [clickedItem, setClickedItem] = useState(null);
    const [open, setOpen] = useState(false);
    const [status, setStatus] = useState("all");
    const [professionalType, setProfessionalType] = useState("all");
    const [sortBy, setSortBy] = useState("createdAt");
    const [sortOrder, setSortOrder] = useState("desc");
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
    const [shouldFetchData, setShouldFetchData] = useState(false);
    const isInitializedRef = useRef(false);

    const dispatch = useAppDispatch();
    const { verifications, verificationsCount, verificationHasNextPage } = useSelector((state: any) => state.admin);
    const isLoading = useSelector(selectLoaderValue);

    // Initialize component
    useEffect(() => {
        isInitializedRef.current = true;
        setShouldFetchData(true);
        getAllVerificationUsers();
    }, []);

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Fetch data when filters change
    useEffect(() => {
        if (shouldFetchData && isInitializedRef.current) {
            console.log("Filters changed, fetching data:", { 
                currentPage, 
                limit, 
                debouncedSearchQuery, 
                requestType, 
                status, 
                professionalType, 
                sortBy, 
                sortOrder 
            });
            getAllVerificationUsers();
        }
    }, [currentPage, limit, debouncedSearchQuery, requestType, status, professionalType, sortBy, sortOrder]);

    // Reset to page 1 when filters change
    useEffect(() => {
        if (shouldFetchData) {
            setCurrentPage(1);
        }
    }, [debouncedSearchQuery, requestType, status, professionalType, sortBy, sortOrder]);

    // Update pagination when verifications data changes
    useEffect(() => {
        if (verifications && verificationsCount) {
            // Use backend pagination info when available
            const calculatedTotalPages = Math.ceil(verificationsCount / limit);
            setTotalPages(calculatedTotalPages);

            // Adjust current page if it's invalid
            if (isInitializedRef.current && currentPage > calculatedTotalPages && calculatedTotalPages > 0) {
                setCurrentPage(calculatedTotalPages);
            }
        } else if (verifications && Array.isArray(verifications)) {
            // Fallback to array length for pagination
            const calculatedTotalPages = Math.ceil(verifications.length / limit);
            setTotalPages(calculatedTotalPages);
        }
    }, [verifications, verificationsCount, limit, currentPage]);

    const getAllVerificationUsers = async () => {
        // Always use the enhanced API with all parameters
        console.log("Fetching verification requests with parameters:", {
            page: currentPage,
            limit,
            search: debouncedSearchQuery,
            requestType,
            status,
            professionalType,
            sortBy,
            sortOrder
        });
        await dispatch(getAllVerifications({
            page: currentPage,
            limit,
            search: debouncedSearchQuery,
            requestType,
            status,
            professionalType,
            sortBy,
            sortOrder,
            isShowLoader: false
        }));
    };

    // Helper function to determine if Next button should be enabled
    const isNextButtonEnabled = () => {
        if (verificationsCount && verificationHasNextPage !== undefined) {
            // Use backend pagination info
            return verificationHasNextPage;
        } else {
            // Fallback to local pagination
            return currentPage < totalPages;
        }
    };
    const getStatusInfo = (verification: any, type: string) => {
        const statusField = type.replace("Files", "Status"); // e.g. identityFiles -> identityStatus
        const statusObj = verification[statusField];
        const statusId = statusObj?.id ? parseInt(statusObj.id, 10) : VerificationStatusEnum.pending;

        // Map enum id to string (capitalized)
        const statusText =
            Object.keys(VerificationStatusEnum).find(
                key => VerificationStatusEnum[key as keyof typeof VerificationStatusEnum] === statusId
            ) || "pending";

        return {
            text: statusText.charAt(0).toUpperCase() + statusText.slice(1), // capitalize
            className: statusStyles[statusId] || statusStyles[VerificationStatusEnum.pending],
        };
    };

    // Backend now returns flattened verification requests, no need to manually flatten
    const verificationRequests = verifications || [];

    // Calculate total number of files across all verification requests
    console.log("Verification requests data:", verificationRequests);
    console.log("VerificationsCount from backend:", verificationsCount);
    console.log("VerificationHasNextPage:", verificationHasNextPage);
    console.log("Current page:", currentPage, "Total pages:", totalPages);
    console.log("Sorting state:", { sortBy, sortOrder, dropdownValue: sortBy + "-" + sortOrder });
    
    // Since backend returns flattened requests, we can count directly
    // Note: For stats, we should get total counts from a separate API call or use verificationsCount
    // For now, we'll count from current page data (this will be incorrect for total stats)
    const totalFiles = verificationsCount || 0; // Use backend total count

    // Count files by status from current page
    const pendingFiles = verificationRequests.filter(request => 
        request.status?.id === "1" || request.status?._id === "1"
    ).length;

    const approvedFiles = verificationRequests.filter(request => 
        request.status?.id === "2" || request.status?._id === "2"
    ).length;

    const rejectedFiles = verificationRequests.filter(request => 
        request.status?.id === "3" || request.status?._id === "3"
    ).length;

    // Note: These stats are based on current page data only
    // For accurate total stats across all pages, we would need a separate stats API endpoint
    return (
        <>
            {isLoading && (
                <div className="fixed left-[50%] top-[40%] z-[1000px]">
                    <Spinner color="green" />
                </div>
            )}
            <div
                className={`min-h-screen bg-gray-50 flex flex-col ${isLoading ? "opacity-25" : "opacity-100"}`}
                style={{ textAlign: "left" }}>
                <AdminHeader />
                <main className="flex-1 lg:pl-64 pt-16">
                    <div className="p-6">
                        <div className="max-w-7xl mx-auto space-y-6">
                            {/* Header */}
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                <div>
                                    <h1 className="text-2xl font-bold text-gray-900">Verification Requests</h1>
                                    <p className="text-gray-600 mt-1">
                                        Review and manage verification requests from professionals
                                    </p>
                                </div>
                                <div className="flex gap-3">
                                    <Button variant="outline" className="flex items-center gap-2">
                                        <Download className="h-4 w-4" />
                                        Export List
                                    </Button>
                                </div>
                            </div>

                            {/* Stats Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <Card className="p-6">
                                    <div className="flex justify-between items-start">
                                        <div className="p-2 bg-blue-100 rounded-lg">
                                            <FileText className="h-5 w-5 text-blue-600" />
                                        </div>
                                        <Badge variant={undefined}>Past 30 days</Badge>
                                    </div>
                                    <h3 className="text-2xl font-semibold mt-4">{totalFiles}</h3>
                                    <p className="text-gray-600">Total Requests</p>
                                </Card>

                                <Card className="p-6">
                                    <div className="flex justify-between items-start">
                                        <div className="p-2 bg-yellow-100 rounded-lg">
                                            <Clock className="h-5 w-5 text-yellow-600" />
                                        </div>
                                        <Badge variant={undefined}>Past 30 days</Badge>
                                    </div>
                                    <h3 className="text-2xl font-semibold mt-4">{pendingFiles}</h3>
                                    <p className="text-gray-600">Pending Review</p>
                                </Card>

                                <Card className="p-6">
                                    <div className="flex justify-between items-start">
                                        <div className="p-2 bg-green-100 rounded-lg">
                                            <CheckCircle className="h-5 w-5 text-green-600" />
                                        </div>
                                        <Badge variant={undefined}>Past 30 days</Badge>
                                    </div>
                                    <h3 className="text-2xl font-semibold mt-4">{approvedFiles}</h3>
                                    <p className="text-gray-600">Approved</p>
                                </Card>

                                <Card className="p-6">
                                    <div className="flex justify-between items-start">
                                        <div className="p-2 bg-red-100 rounded-lg">
                                            <AlertTriangle className="h-5 w-5 text-red-600" />
                                        </div>
                                        <Badge variant={undefined}>Past 30 days</Badge>
                                    </div>
                                    <h3 className="text-2xl font-semibold mt-4">{rejectedFiles}</h3>
                                    <p className="text-gray-600">Rejected</p>
                                </Card>
                            </div>

                            {/* Main Content Card */}
                            <Card className="overflow-hidden">
                                {/* Search and Filters */}
                                <div className="p-4 border-b">
                                    <div className="flex flex-col sm:flex-row justify-between gap-4">
                                        <div className="flex items-center gap-4">
                                            <div className="relative flex-1 sm:min-w-[300px]">
                                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                                <input
                                                    type="text"
                                                    placeholder="Search requests..."
                                                    value={searchQuery}
                                                    onChange={e => setSearchQuery(e.target.value)}
                                                    className="w-full pl-10 pr-4 py-2 border rounded-lg"
                                                />
                                                {searchQuery !== debouncedSearchQuery && (
                                                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                                    </div>
                                                )}
                                            </div>
                                            <Button
                                                variant="outline"
                                                onClick={() => setShowFilters(!showFilters)}
                                                className="flex items-center gap-2">
                                                <Filter className="h-5 w-5" />
                                                Filters
                                            </Button>
                                        </div>

                                        <div className="flex items-center gap-4">
                                            <select
                                                value={sortBy + "-" + sortOrder}
                                                onChange={e => {
                                                    const [newSortBy, newSortOrder] = e.target.value.split("-");
                                                    console.log("Sort dropdown changed:", { 
                                                        newSortBy, 
                                                        newSortOrder, 
                                                        fullValue: e.target.value,
                                                        currentSortBy: sortBy,
                                                        currentSortOrder: sortOrder
                                                    });
                                                    setSortBy(newSortBy);
                                                    setSortOrder(newSortOrder);
                                                }}
                                                className="px-4 py-2 border rounded-lg">
                                                <option value="createdAt-desc">Newest First</option>
                                                <option value="createdAt-asc">Oldest First</option>
                                                <option value="updatedAt-desc">Recently Updated</option>
                                                <option value="updatedAt-asc">Least Recently Updated</option>
                                            </select>
                                        </div>
                                    </div>

                                    <AnimatePresence>
                                        {showFilters && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                className="mt-4 pt-4 border-t">
                                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Request Type
                                                        </label>
                                                        <select
                                                            value={requestType}
                                                            onChange={e => setRequestType(e.target.value)}
                                                            className="w-full px-4 py-2 border rounded-lg">
                                                            <option value="all">All Types</option>
                                                            <option value="identity">Identity Verification</option>
                                                            <option value="business">Business Verification</option>
                                                            <option value="addressProof">Address Proof</option>
                                                        </select>
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Status
                                                        </label>
                                                        <select
                                                            value={status}
                                                            onChange={e => setStatus(e.target.value)}
                                                            className="w-full px-4 py-2 border rounded-lg">
                                                            <option value="all">All Status</option>
                                                            <option value="pending">Pending</option>
                                                            <option value="approved">Approved</option>
                                                            <option value="rejected">Rejected</option>
                                                        </select>
                                                    </div>

                                                    {/* <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Professional Type
                                                        </label>
                                                        <select
                                                            value={professionalType}
                                                            onChange={e => setProfessionalType(e.target.value)}
                                                            className="w-full px-4 py-2 border rounded-lg">
                                                            <option value="all">All Types</option>
                                                            <option value="local">Local Professional</option>
                                                            <option value="international">
                                                                International Professional
                                                            </option>
                                                        </select>
                                                    </div> */}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>

                                {/* Requests Table */}
                                <div className="overflow-x-auto">
                                    <VerificationRequestTable
                                        verifications={verifications}
                                        getStatusInfo={getStatusInfo}
                                        setCurrentUserInfo={setCurrentUserInfo}
                                        setOpen={setOpen}
                                        setClickedItem={setClickedItem}
                                    />
                                </div>

                                {/* Pagination */}
                                <div className="px-6 py-4 border-t flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <span className="text-sm text-gray-700">
                                            Showing <span className="font-medium">{verificationsCount > 0 ? ((currentPage - 1) * limit) + 1 : 0}</span> to{" "}
                                            <span className="font-medium">
                                                {Math.min(currentPage * limit, verificationsCount || 0)}
                                            </span>{" "}
                                            of <span className="font-medium">{verificationsCount || 0}</span> results
                                        </span>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-gray-700">Show:</span>
                                            <select
                                                value={limit}
                                                onChange={(e) => {
                                                    const newLimit = parseInt(e.target.value);
                                                    setLimit(newLimit);
                                                }}
                                                className="text-sm border border-gray-300 rounded-md px-2 py-1"
                                            >
                                                <option value={5}>5</option>
                                                <option value={10}>10</option>
                                                <option value={20}>20</option>
                                                <option value={50}>50</option>
                                            </select>
                                            <span className="text-sm text-gray-700">per page</span>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                            disabled={currentPage === 1}>
                                            Previous
                                        </Button>
                                        <span className="flex items-center px-3 text-sm text-gray-700">
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setCurrentPage(currentPage + 1)}
                                            disabled={!isNextButtonEnabled()}>
                                            Next
                                        </Button>
                                    </div>
                                </div>
                            </Card>
                        </div>
                    </div>
                </main>
                <br></br>
                <br></br>
                <br></br>
                <br></br>

                {/* Modals */}
                <AnimatePresence>
                    <VerificationDetailModal
                        isOpen={open}
                        onClose={() => setOpen(false)}
                        request={currentUserInfo}
                        clickedItem={clickedItem}
                        onRefresh={getAllVerificationUsers}
                    />
                </AnimatePresence>

                {/* Footer */}
                {/* <SharedFooter3 /> */}
            </div>
        </>
    );
};

export default VerificationRequestsPage;
